import React, { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import SeoHead from '../components/SeoHead';
import { captureFeedbackEvent, openSentryReportDialog } from '../utils/sentry';
import './FeedbackPage.css';

const CATEGORIES = [
  { id: 'bug', label: 'Bug' },
  { id: 'idea', label: 'Idea' },
  { id: 'account', label: 'Account / sign-in' },
  { id: 'ad', label: 'Ads' },
  { id: 'other', label: 'Other' },
];

export default function FeedbackPage() {
  const [category, setCategory] = useState('bug');
  const [message, setMessage] = useState('');
  const [email, setEmail] = useState('');
  const [includeTech, setIncludeTech] = useState(true);
  const [submittedId, setSubmittedId] = useState(null);
  const [error, setError] = useState(null);

  const sentryEnabled = Boolean(process.env.REACT_APP_SENTRY_DSN?.trim()) && process.env.NODE_ENV === 'production';

  const source = useMemo(() => {
    try {
      const lastPath = window.sessionStorage.getItem('lq:last_path');
      const lastHref = window.sessionStorage.getItem('lq:last_href');
      return {
        path: lastPath || null,
        href: lastHref || null,
      };
    } catch {
      return { path: null, href: null };
    }
  }, []);

  const diagnosticsText = useMemo(() => {
    return [
      `Reported from: ${source.path || '—'}`,
      `Reported from URL: ${source.href || '—'}`,
      `Feedback page: ${window.location.pathname}`,
      `Referrer: ${document.referrer || '—'}`,
      `Theme: ${document.documentElement.getAttribute('data-theme') || '—'}`,
      `Viewport: ${window.innerWidth}x${window.innerHeight}`,
      `UA: ${navigator.userAgent}`,
    ].join('\n');
  }, [source.path, source.href]);

  const canSubmit = message.trim().length >= 10;

  const submit = (e) => {
    e.preventDefault();
    setError(null);

    if (!sentryEnabled) {
      setError('Feedback is not configured yet. Add the Sentry DSN in production to enable submissions.');
      return;
    }
    if (!canSubmit) {
      setError('Please add a bit more detail (at least 10 characters).');
      return;
    }

    const eventId = captureFeedbackEvent({
      category,
      message: message.trim(),
      email: email.trim(),
      includeTechDetails: includeTech,
      extra: {
        referrer: document.referrer || undefined,
        sourcePath: source.path || undefined,
        sourceHref: source.href || undefined,
      },
    });

    setSubmittedId(eventId);
    setMessage('');
  };

  return (
    <main className="feedback-page">
      <SeoHead
        title="Feedback"
        description="Send feedback about Letter Quest: report bugs, suggest improvements, and share ideas. Optional contact email. Includes technical details only if you opt in."
        path="/feedback"
      />

      <h1>Feedback</h1>
      <p className="feedback-page__lead">
        Found a bug or have an idea? Send it here. You can optionally include technical details to help us reproduce the
        issue. For policies, see <Link to="/privacy">Privacy</Link>.
      </p>

      {source.path && (
        <p className="feedback-page__from">
          You’re reporting feedback from <strong>{source.path}</strong>.
        </p>
      )}

      {!sentryEnabled && (
        <div className="feedback-page__notice">
          <strong>Not configured:</strong> This form will work once the site is deployed with a Sentry DSN (production
          only).
        </div>
      )}

      {submittedId && (
        <div className="feedback-page__success" role="status">
          Thanks — your feedback was sent.
          <button
            type="button"
            className="feedback-page__report-btn"
            onClick={() => openSentryReportDialog(submittedId)}
            disabled={!sentryEnabled}
          >
            Add contact details
          </button>
        </div>
      )}

      {error && (
        <div className="feedback-page__error" role="alert">
          {error}
        </div>
      )}

      <form className="feedback-form" onSubmit={submit}>
        <label className="feedback-form__label">
          Category
          <select value={category} onChange={(e) => setCategory(e.target.value)} className="feedback-form__select">
            {CATEGORIES.map((c) => (
              <option key={c.id} value={c.id}>
                {c.label}
              </option>
            ))}
          </select>
        </label>

        <label className="feedback-form__label">
          What happened?
          <textarea
            value={message}
            onChange={(e) => setMessage(e.target.value)}
            className="feedback-form__textarea"
            placeholder="Tell us what you expected vs what happened, and the steps to reproduce."
            rows={7}
          />
          <span className="feedback-form__hint">Tip: include the game name and what you clicked.</span>
        </label>

        <label className="feedback-form__label">
          Email (optional)
          <input
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            className="feedback-form__input"
            type="email"
            placeholder="you@example.com"
          />
          <span className="feedback-form__hint">Only used to respond to this feedback.</span>
        </label>

        <label className="feedback-form__checkbox">
          <input type="checkbox" checked={includeTech} onChange={(e) => setIncludeTech(e.target.checked)} />
          Include technical details (page, browser, screen size)
        </label>

        <details className="feedback-form__details">
          <summary>Preview technical details</summary>
          <pre className="feedback-form__pre">{diagnosticsText}</pre>
        </details>

        <div className="feedback-form__actions">
          <button type="submit" className="feedback-form__submit" disabled={!canSubmit}>
            Send feedback
          </button>
          <Link to="/" className="feedback-form__cancel">
            Cancel
          </Link>
        </div>
      </form>
    </main>
  );
}

